<template>
  <QaPageShell :title="$t('_QAInvestigations')" :loading="loading" :error="error" @retry="load">
    <template #filters>
      <QaFilterBar @change="load" />
    </template>

    <!-- ชนเพดาน 20,000 เคส — ห้ามเงียบ ตัวเลขสรุปด้านล่างคิดจากเท่าที่ดึงมาเท่านั้น
         ถ้าไม่บอก คนจะอ่านว่า "มีเคสต้องสอบสวนเท่านี้" ทั้งที่ของจริงมีมากกว่า -->
    <v-alert v-if="truncated" type="warning" variant="tonal" density="compact" class="mt-3" icon="mdi-alert-outline">
      <span class="text-body-2">
        ช่วง {{ windowDays }} วันมีเคสเกินเพดาน 20,000 เคส — ตัวเลขและรายการข้างล่างคิดจากเท่าที่ดึงมา ลองแคบช่วงเวลาลง
      </span>
    </v-alert>

    <v-row class="mt-1" dense>
      <v-col v-for="card in summaryCards" :key="card.key" cols="12" sm="4">
        <v-card class="qa-card pa-4" variant="flat">
          <div class="d-flex align-center ga-2">
            <span class="text-caption text-medium-emphasis">{{ card.label }}</span>
            <!-- ยังไม่เปิดการตรวจ compliance gate → ค่านี้เป็น 0 ตลอด · ป้ายไว้ตรงจุด
                 ไม่งั้น "0" จะถูกอ่านว่า "ไม่มีเคสตกเลย" ซึ่งเป็นข่าวดีปลอม -->
            <QaMockChip v-if="card.mockReason" text="ยังไม่ตรวจ" :reason="card.mockReason" />
          </div>
          <div class="text-h4 font-weight-medium mt-1 qa-kpi-value" :class="card.class">{{ card.value }}</div>
          <div class="text-caption text-medium-emphasis">{{ card.sub }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="qa-card mt-3" variant="flat">
      <div class="d-flex align-center flex-wrap ga-3 pa-3">
        <v-btn-toggle v-model="filter" density="comfortable" variant="outlined" divided mandatory rounded="lg" color="primary" @update:model-value="load">
          <v-btn value="all" size="small">ทั้งหมด</v-btn>
          <v-btn value="gate" size="small">ตก Gate</v-btn>
          <v-btn value="low" size="small">คะแนนต่ำ</v-btn>
          <!-- ⚠️ ต้องมีปุ่มนี้ — "คะแนนต่ำ" นับเฉพาะเคสที่ตกจริง (ให้ตรงกับการ์ดด้านบน)
               ถ้าไม่มีถังของเฉียดเกณฑ์ เคสกลุ่มนั้นจะเข้าถึงได้แค่ผ่านปุ่ม "ทั้งหมด" -->
          <v-btn value="borderline" size="small">เฉียดเกณฑ์</v-btn>
        </v-btn-toggle>
        <v-spacer />
        <span class="text-caption text-medium-emphasis">{{ total }} เคส</span>
      </div>
      <v-divider />

      <!-- ป้ายเดียวกับการ์ดด้านบน แต่ต้องมีตรงนี้ด้วย: คนที่กด "ตก Gate" แล้วเจอตารางว่าง
           ต้องรู้ว่าเป็นเพราะยังไม่เปิดการตรวจ ไม่ใช่เพราะเจ้าหน้าที่ทำได้ดีทุกเคส -->
      <v-alert v-if="filter === 'gate'" type="info" variant="tonal" density="compact" class="ma-3" icon="mdi-flask-outline">
        <span class="text-body-2">
          ยังไม่เปิดการตรวจ compliance gate — ไม่มีเคสไหนเข้าเงื่อนไขนี้ได้จนกว่าจะเปิด (รายการจึงว่างเสมอ)
        </span>
      </v-alert>

      <div class="qa-table-wrap pt-3">
        <v-data-table
          :headers="headers"
          :items="items"
          :items-per-page="25"
          class="elevation-0 my-data-table qa-table qa-table-wide"
          density="comfortable"
          fixed-header
          hover
          :row-props="{ class: 'qa-row-click' }"
          @click:row="openCase"
        >
          <template #[`item.priority`]="{ item }">
            <v-chip :color="priorityColor(item.priority)" size="small" variant="tonal" label class="font-weight-medium">{{ priorityLabel(item.priority) }}</v-chip>
          </template>
          <template #[`item.evaluatedAt`]="{ item }">
            <span class="qa-num text-body-2">{{ formatDateShort(item.evaluatedAt) }}</span>
          </template>
          <!-- channel = CaseType ของเคส (voice/chat) ไม่ใช่ช่องทางโซเชียล -->
          <template #[`item.channel`]="{ item }"><QaChannelChip :channel="item.channel || 'chat'" /></template>
          <!-- ⚠️ ไม่มีอวาตาร์แล้ว — server ไม่ได้ส่ง agentInitials/agentColor มา และห้าม
               ปั้นสีเองในหน้าจอ (สีต้องคงที่ต่อคนทั้งระบบ ถ้าแต่ละหน้าคิดเอง คนเดียวกัน
               จะได้คนละสีคนละหน้า) · ชื่ออย่างเดียวจึงตรงกว่าอวาตาร์ที่ผิด -->
          <template #[`item.agentName`]="{ item }">
            <span class="text-body-2 font-weight-medium">{{ item.agentName || 'ยังไม่มีเจ้าหน้าที่รับ' }}</span>
          </template>
          <template #[`item.rootIssue`]="{ item }">
            <span class="text-body-2 text-medium-emphasis">{{ item.rootIssue || '—' }}</span>
          </template>
          <!-- totalScore เป็น null ได้ (เคสที่ต้องสอบสวนเพราะ gate/รอคน ไม่ใช่เพราะคะแนน)
               QaScoreCell แสดง "—" ให้เอง — อย่าแทนด้วย 0 -->
          <template #[`item.totalScore`]="{ item }"><QaScoreCell :score="item.totalScore" :status="item.qaStatus" /></template>
          <template #[`item.actions`]>
            <span class="text-primary text-body-2 font-weight-medium">{{ $t('qaInvestigate') }} →</span>
          </template>
          <template #no-data>
            <div class="text-center py-8 text-medium-emphasis">
              <v-icon size="34" color="success">mdi-check-circle-outline</v-icon>
              <div class="text-body-2 mt-2">ไม่มีเคสค้างตรวจสอบ</div>
              <div class="text-caption mt-1">ในช่วง {{ windowDays }} วันที่เลือก ทุกเคสที่ประเมินแล้วผ่านเกณฑ์</div>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card>
  </QaPageShell>
</template>

<script>
/**
 * เคสที่ต้องตรวจสอบ — ข้อมูลจริงจาก GET analytics/investigations
 *
 * ⚠️ ตัวกรอง 2 ชั้นคนละแกนกัน อย่าสลับ:
 *      • ตัวกรองร่วมของ QA Console (ช่องทาง/หัวข้อ/ช่วงเวลา) อยู่ใน Vuex → argument ที่ 1
 *      • ปุ่ม ทั้งหมด/ตก Gate/คะแนนต่ำ เป็น bucket ของหน้านี้เท่านั้น → argument ที่ 2
 *    ทั้งคู่ชื่อ "filter" ในภาษาพูด เลยเคยถูกยัดรวมเป็นก้อนเดียวส่ง server มาก่อน
 */
import qaConsole from '@/services/qaConsole';
import { formatDateShort, priorityLabel, priorityColor } from '@/utils/qaFormat';
import QaPageShell from '@/components/QAConsole/QaPageShell.vue';
import QaFilterBar from '@/components/QAConsole/QaFilterBar.vue';
import QaScoreCell from '@/components/QAConsole/QaScoreCell.vue';
import QaChannelChip from '@/components/QAConsole/QaChannelChip.vue';
import QaMockChip from '@/components/QAConsole/QaMockChip.vue';

export default {
  name: 'QAInvestigations',
  components: { QaPageShell, QaFilterBar, QaScoreCell, QaChannelChip, QaMockChip },
  data() {
    return {
      loading: true,
      error: null,
      items: [],
      total: 0,
      truncated: false,
      windowDays: 30,
      summary: { gateFail: 0, lowScore: 0, borderline: 0 },
      filter: 'all',
    };
  },
  computed: {
    summaryCards() {
      return [
        {
          key: 'gate',
          label: 'ตก compliance gate',
          value: this.summary.gateFail,
          sub: 'ตกทันทีไม่ว่าคะแนนเท่าไหร่',
          class: 'text-error',
          mockReason: 'ยังไม่เปิดการตรวจ compliance gate — ค่านี้จึงเป็น 0 เสมอ ไม่ได้แปลว่าไม่มีเคสตก',
        },
        { key: 'low', label: 'ไม่ผ่านเพราะคะแนน', value: this.summary.lowScore, sub: 'คะแนนรวมต่ำกว่าเกณฑ์', class: 'text-warning', mockReason: '' },
        { key: 'border', label: 'เฉียดเกณฑ์', value: this.summary.borderline, sub: 'ผ่านแต่ควรดูซ้ำ', class: '', mockReason: '' },
      ];
    },
    headers() {
      return [
        { title: this.$t('qaPriority'), key: 'priority', sortable: false, width: '110px' },
        { title: this.$t('qaDate'), key: 'evaluatedAt', width: '100px' },
        { title: this.$t('qaType'), key: 'channel', sortable: false, width: '110px' },
        { title: this.$t('qaAgent'), key: 'agentName', sortable: false, minWidth: '180px' },
        { title: this.$t('qaCaseReason'), key: 'reason', minWidth: '200px' },
        { title: this.$t('qaRootIssue'), key: 'rootIssue', minWidth: '190px' },
        { title: this.$t('qaQaScore'), key: 'totalScore', align: 'end', width: '110px' },
        { title: '', key: 'actions', sortable: false, align: 'end', width: '120px' },
      ];
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDateShort,
    priorityLabel,
    priorityColor,
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await qaConsole.getInvestigations(this.$store.getters['qaConsole/filter'], this.filter);
        this.items = res.items || [];
        this.total = res.total ?? this.items.length;
        this.summary = res.summary || { gateFail: 0, lowScore: 0, borderline: 0 };
        this.truncated = !!res.truncated;
        // ⚠️ ใช้ windowDays ที่ server ตอบกลับ ไม่ใช่ค่าที่เลือกในหน้าจอ — "ทั้งหมด"
        //    ถูกบีบเป็น 365 วันฝั่ง server ข้อความจึงต้องพูดตามช่วงที่คิดจริง
        this.windowDays = res.windowDays ?? this.windowDays;
      } catch (err) {
        this.error = err;
      } finally {
        this.loading = false;
      }
    },
    openCase(_event, { item }) {
      this.$router.push({ name: 'QACaseDetail', query: { caseNo: item.caseNo, from: 'QAInvestigations' } });
    },
  },
};
</script>
